// ARCA-81 WS1 — take-two screen shell, TopBar & back-header.
//
// Foundational structural primitives that replace the per-screen inline shells
// duplicated across Settings / Profile / Leaderboard / Tournaments / About /
// Friends / Replay. Ported from the take-two prototype:
//   • Screen / .screen-scroll → "Docs/UI-prototype take two/project/styles.css"
//     (.screen / .screen-scroll, lines 120-126)
//   • TopBar (3-slot)         → "…/src/ui.jsx" TopBar (lines 175-183, pad 8 16 6)
//   • ScreenHeader (the Head) → "…/src/Misc.jsx" Head (lines 6-14): a back
//     IconBtn + centred Fredoka-18 title + a right action / 46px spacer.
//
// The recipes (.screen / .screen-scroll / .topbar / .screen-head /
// .screen-title / .btn-icon) live in pieces.css. The hub (Lobby) keeps its own
// currency TopBar and gets NO back-header; every other screen routes through
// ScreenHeader so there is a single canonical back control.
//
// The back glyph is an inline SVG for now — the shared Icon set lands in WS2
// (ARCA-82); this consumer swaps over to <Icon name="back"> then.

import type { CSSProperties, HTMLAttributes, JSX, ReactNode } from 'react'
import { playSfx } from '../../game/audio/useAudio.js'

// ---------------------------------------------------------------------------
// IconButton — round-ish .btn-icon chrome (back control + future icon actions).
// ---------------------------------------------------------------------------
export interface IconButtonProps {
  readonly children: ReactNode
  /** Required: the button carries an icon, never a text label. */
  readonly ariaLabel: string
  readonly onClick?: () => void
  readonly className?: string
  readonly testId?: string
}

export function IconButton({
  children,
  ariaLabel,
  onClick,
  className,
  testId,
}: IconButtonProps): JSX.Element {
  const classes = ['btn-icon', className].filter(Boolean).join(' ')
  return (
    <button
      type="button"
      className={classes}
      aria-label={ariaLabel}
      // Tactile cue parity with <Btn>: pointer press + keyboard activation.
      onPointerDown={() => {
        playSfx('ui-tap')
      }}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !e.repeat) {
          playSfx('ui-tap')
        }
      }}
      onClick={onClick}
      data-testid={testId}
    >
      {children}
    </button>
  )
}

// ---------------------------------------------------------------------------
// TopBar — 3-slot row (left / center / right). Prototype pad 8 16 6.
// ---------------------------------------------------------------------------
export interface TopBarProps {
  readonly left?: ReactNode
  readonly center?: ReactNode
  readonly right?: ReactNode
  readonly className?: string
  readonly style?: CSSProperties
}

export function TopBar({
  left,
  center,
  right,
  className,
  style,
}: TopBarProps): JSX.Element {
  const classes = ['topbar', className].filter(Boolean).join(' ')
  return (
    <div className={classes} style={style}>
      <div className="topbar-left">{left}</div>
      <div className="topbar-center">{center}</div>
      <div className="topbar-right">{right}</div>
    </div>
  )
}

// Back chevron (prototype icons.jsx `back`: "M15 5l-7 7 7 7"). Decorative —
// the wrapping IconButton owns the accessible label.
function BackChevron(): JSX.Element {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M15 5l-7 7 7 7" />
    </svg>
  )
}

// ---------------------------------------------------------------------------
// ScreenHeader (the "Head") — status-clearance wrapper + back IconBtn +
// centred Fredoka-18 <h1> title + right action / 46px spacer.
// ---------------------------------------------------------------------------
export interface ScreenHeaderProps {
  readonly title: ReactNode
  /** Canonical back control — every non-hub screen wires this. */
  readonly onBack: () => void
  /** Accessible label for the back control (localised by the caller). */
  readonly backLabel: string
  /** Optional id so a screen can aria-labelledby the title. */
  readonly titleId?: string
  /** Optional right-slot action; defaults to a 46px spacer that balances back. */
  readonly right?: ReactNode
}

export function ScreenHeader({
  title,
  onBack,
  backLabel,
  titleId,
  right,
}: ScreenHeaderProps): JSX.Element {
  return (
    <header className="screen-head">
      <TopBar
        left={
          <IconButton
            ariaLabel={backLabel}
            onClick={onBack}
            testId="screen-back"
          >
            <BackChevron />
          </IconButton>
        }
        center={
          <h1 className="screen-title" id={titleId}>
            {title}
          </h1>
        }
        right={
          right ?? <span className="screen-head-spacer" aria-hidden="true" />
        }
      />
    </header>
  )
}

// ---------------------------------------------------------------------------
// Screen — the shell. A viewport-tall flex column (.screen) carrying the
// header above a scrolling content region (.screen-scroll = <main id="main">,
// the router focus + skip-link target). The per-screen gutter (16 dominant,
// 18 settings/profile, 22 about) is stamped via the --screen-gutter prop.
// ---------------------------------------------------------------------------
type ScreenMainProps = Omit<
  HTMLAttributes<HTMLElement>,
  'id' | 'className' | 'tabIndex' | 'style'
> & {
  readonly style?: CSSProperties
  readonly [dataAttr: `data-${string}`]: string | number | undefined
}

export interface ScreenProps {
  /** Screen id → data-screen + className suffix (e.g. "settings"). */
  readonly name: string
  /** The back-header (ScreenHeader) — omitted only by the hub. */
  readonly header?: ReactNode
  /** Horizontal gutter for .screen-scroll. 16 dominant · 18 settings/profile · 22 about. */
  readonly gutter?: 16 | 18 | 22
  /** Extra layout for the scroll column (gap / alignItems / justifyContent). */
  readonly contentStyle?: CSSProperties
  /** data- and aria- attributes forwarded onto <main> (data-state, data-…-id, aria-label). */
  readonly mainProps?: ScreenMainProps
  readonly children: ReactNode
}

export function Screen({
  name,
  header,
  gutter = 16,
  contentStyle,
  mainProps,
  children,
}: ScreenProps): JSX.Element {
  const { style: mainStyle, ...restMain } = mainProps ?? {}
  return (
    <div className={`screen rg-screen ${name}`} data-screen={name}>
      {header}
      <main
        {...restMain}
        id="main"
        tabIndex={-1}
        className="screen-scroll"
        style={
          {
            // Custom property → per-screen gutter (consumed by .screen-scroll).
            '--screen-gutter': `${gutter}px`,
            ...contentStyle,
            ...mainStyle,
          } as CSSProperties
        }
      >
        {children}
      </main>
    </div>
  )
}
